/**
 * Dirty-node tracking for selective 3D geometry rebuilds.
 *
 * Compares two BuildInput snapshots and returns which subsystem groups
 * need to be rebuilt. Viewer3D uses this to skip unchanged geometry.
 */
import { DEFAULT_FINISHES, type FinishConfig } from "../domain/finishes/types";
import { resolveWallHeightM } from "../domain/geometry/buildingDimensions";
import { normalizeIndoorRoom } from "../domain/indoor/indoorRoom";
import { resolveFlueAxisM } from "./chimneyFlue";
import type { BuildInput } from "./types";
import { interiorLayoutsEqual } from "../domain/interior/planEditing";

// ─── Dirty Flags ────────────────────────────────────────────

export const DirtyFlag = {
	Foundation: "foundation",
	Deck: "deck",
	Framing: "framing",
	Panel: "panel",
	Roof: "roof",
	Interior: "interior",
	Exterior: "exterior",
} as const;

export type DirtyFlag = (typeof DirtyFlag)[keyof typeof DirtyFlag];

/** Every flag — used as "full rebuild needed". */
export const ALL_DIRTY: ReadonlySet<DirtyFlag> = new Set(
	Object.values(DirtyFlag),
);

// ─── Dependency Map ─────────────────────────────────────────
// Documents which input fields affect which geometry groups.

export const DEPENDENCY_MAP: Record<string, readonly DirtyFlag[]> = {
	dimensions: [...ALL_DIRTY],
	// Interior: the interior wall layer stack (insulation bays, bastufolie,
	// liggande glespanel — see squareBaseBuilder/interior.ts) is split around
	// openings, so moving/adding one must rebuild it or the folie and batts keep
	// the previous opening's holes.
	openings: [
		DirtyFlag.Foundation,
		DirtyFlag.Framing,
		DirtyFlag.Panel,
		DirtyFlag.Interior,
		DirtyFlag.Exterior,
	],
	partitionSplit: [
		DirtyFlag.Framing,
		DirtyFlag.Panel,
		DirtyFlag.Interior,
		DirtyFlag.Exterior,
	],
	// Exterior: the facade suppresses base trim on deck-attached walls, so a
	// deck change must rebuild the exterior too (see squareBaseBuilder/facade.ts
	// baseTrimSuppressedWalls) — otherwise stale base trim clips the deck.
	deckConfig: [DirtyFlag.Deck, DirtyFlag.Exterior],
	roofConfig: [
		DirtyFlag.Framing,
		DirtyFlag.Panel,
		DirtyFlag.Roof,
		DirtyFlag.Interior,
		DirtyFlag.Exterior,
	],
	thermalInputs: [DirtyFlag.Interior],
	// The lavar and the aggregat are interior geometry, and nothing else.
	// (The renderer starts reading this in #131; the flag has to be correct from
	// the moment the field exists, or the first render that uses it inherits a
	// stale-geometry bug that looks like a rendering problem.)
	interiorLayout: [DirtyFlag.Interior],
	// Panel direction is the only karaktärsval that moves geometry: the
	// bastupanel is one textured slab per wall either way, but the korsläkt
	// under it flips (lying rows ↔ standing runs) and the slab is built with a
	// different material instance (the 90°-rotated texture variant). Interior
	// owns the battens, Panel owns the slab.
	finishes: [DirtyFlag.Panel, DirtyFlag.Interior],
	siteConditions: [DirtyFlag.Foundation],
	// Inomhus (issue 174): which walls stand against the room decides board vs
	// panel + foder, all owned by `buildIndoorOuterLayers` in Exterior.
	indoorRoom: [DirtyFlag.Exterior],
	// The house height above an indoor sauna sets how many chimney modules are
	// bought, and the interior draws exactly those (chimneyGeometry.ts).
	houseAboveCeiling: [DirtyFlag.Interior],
	entryWall: [
		DirtyFlag.Foundation,
		DirtyFlag.Framing,
		DirtyFlag.Panel,
		DirtyFlag.Interior,
		DirtyFlag.Exterior,
	],
	glazingStyle: [
		DirtyFlag.Framing,
		DirtyFlag.Panel,
		DirtyFlag.Interior,
		DirtyFlag.Exterior,
	],
	saunaPosition: [DirtyFlag.Interior],
	hasRelaxRoom: [
		DirtyFlag.Foundation,
		DirtyFlag.Framing,
		DirtyFlag.Panel,
		DirtyFlag.Interior,
		DirtyFlag.Exterior,
	],
};

const TWO_ROOM_CONFIG_DEFAULTS = {
	entryWall: "short",
	glazingStyle: "standard",
	saunaPosition: "left",
	hasRelaxRoom: true,
} as const;

type TwoRoomConfigKey = keyof typeof TWO_ROOM_CONFIG_DEFAULTS;

// ─── Comparison Helpers ─────────────────────────────────────

function addDirtyDependencies(
	dirty: Set<DirtyFlag>,
	dependencyKey: string,
): void {
	for (const flag of DEPENDENCY_MAP[dependencyKey]) {
		dirty.add(flag);
	}
}

/**
 * Structural equality for an input group.
 *
 * Hand-written field-by-field predicates rot: they silently ignore whichever
 * field the type gained (or already had) since they were last touched, and the
 * rebuild scheduler cannot see what the predicate does not compare. That gap
 * shipped twice — `thermalEqual` missed `heaterPlacementZMm`, `openingsEqual`
 * missed `glazed` — so every group compares structurally instead.
 *
 * Two deliberate differences from a `JSON.stringify` compare:
 *
 * - **Key order is irrelevant.** `thermalInputs` and `siteConditions` are built
 *   at several sites (`useBuilderActions`, `useBuilderDesignLifecycle`,
 *   `mappers` rehydrating a saved design), so two equal snapshots can serialise
 *   to different strings and force a needless full rebuild.
 * - **An absent key equals an explicitly `undefined` one**, which is the same
 *   seed-vs-editor asymmetry that makes optional booleans like `glazed` need
 *   coercion.
 */
function inputGroupEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true;

	// An optional boolean's absence means `false` — seeds omit `glazed` where
	// OpeningsEditor writes an explicit `false`, and the difference must not
	// read as an edit. Deliberately NOT generalised to falsy values: an absent
	// `heaterPlacementZMm` is not the same as a heater at Z = 0.
	if (a === undefined && typeof b === "boolean") return b === false;
	if (b === undefined && typeof a === "boolean") return a === false;

	if (
		a === null ||
		b === null ||
		typeof a !== "object" ||
		typeof b !== "object"
	) {
		return false;
	}

	const aIsArray = Array.isArray(a);
	if (aIsArray !== Array.isArray(b)) return false;

	if (aIsArray) {
		const av = a as unknown[];
		const bv = b as unknown[];
		if (av.length !== bv.length) return false;
		return av.every((value, index) => inputGroupEqual(value, bv[index]));
	}

	const ao = a as Record<string, unknown>;
	const bo = b as Record<string, unknown>;
	const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
	for (const key of keys) {
		if (!inputGroupEqual(ao[key], bo[key])) return false;
	}
	return true;
}

/**
 * Footprint AND wall height (issue 155): a height change moves every wall,
 * the roof, the lavar and the opening reveals, exactly like a resize. Compared
 * resolved, so an unset height and an explicit 2.1 m are the same scene.
 */
function dimensionsEqual(a: BuildInput, b: BuildInput): boolean {
	return (
		a.dimensions.width === b.dimensions.width &&
		a.dimensions.length === b.dimensions.length &&
		resolveWallHeightM(a.wallHeightM) === resolveWallHeightM(b.wallHeightM)
	);
}

function openingsEqual(a: BuildInput, b: BuildInput): boolean {
	return inputGroupEqual(a.openings, b.openings);
}

function thermalEqual(a: BuildInput, b: BuildInput): boolean {
	return inputGroupEqual(a.thermalInputs, b.thermalInputs);
}

function roofConfigEqual(a: BuildInput, b: BuildInput): boolean {
	return inputGroupEqual(a.roofConfig ?? null, b.roofConfig ?? null);
}

function partitionSplitEqual(a: BuildInput, b: BuildInput): boolean {
	return (a.partitionSplit ?? 0.5) === (b.partitionSplit ?? 0.5);
}

function deckConfigEqual(a: BuildInput, b: BuildInput): boolean {
	return inputGroupEqual(a.deckConfig ?? null, b.deckConfig ?? null);
}

function siteConditionsEqual(a: BuildInput, b: BuildInput): boolean {
	return inputGroupEqual(a.siteConditions ?? null, b.siteConditions ?? null);
}

function interiorLayoutEqual(a: BuildInput, b: BuildInput): boolean {
	// Authored bench options are tri-state: absent skirtEnabled is automatic,
	// while false removes boards. The generic optional-boolean default loses this edit.
	return interiorLayoutsEqual(a.interiorLayout ?? null, b.interiorLayout ?? null);
}

/**
 * Karaktärsval split by what they actually cost to apply.
 *
 * `panelDirection` changes the built scene, so it has to schedule a rebuild.
 * Wood species and exterior treatment only restyle SHARED materials
 * (`applyFinishSelection` in materials.ts) — rebuilding the walls to change a
 * tint would throw away geometry that did not move.
 *
 * `Record<keyof FinishConfig, …>` rather than two arrays: a fourth choice is a
 * COMPILE error until someone picks a side. A hand-written predicate that
 * silently ignores a new field is the failure this repo has already shipped
 * twice (`thermalEqual` missing `heaterPlacementZMm`, `openingsEqual` missing
 * `glazed`).
 */
const FINISH_EFFECTS: Record<keyof FinishConfig, "geometry" | "material"> = {
	panelDirection: "geometry",
	interiorWood: "material",
	exteriorTreatment: "material",
};

const GEOMETRY_AFFECTING_FINISH_KEYS = (
	Object.keys(FINISH_EFFECTS) as Array<keyof FinishConfig>
).filter((key) => FINISH_EFFECTS[key] === "geometry");

function indoorWallsEqual(a: BuildInput, b: BuildInput): boolean {
	return (
		normalizeIndoorRoom(a.indoorRoom).existingWalls.join() ===
		normalizeIndoorRoom(b.indoorRoom).existingWalls.join()
	);
}

function finishesEqual(a: BuildInput, b: BuildInput): boolean {
	// Plain `!==`: every field is a string literal union, so the deep,
	// undefined-tolerant `inputGroupEqual` would only cost stack frames.
	return GEOMETRY_AFFECTING_FINISH_KEYS.every(
		(key) =>
			(a.finishes?.[key] ?? DEFAULT_FINISHES[key]) ===
			(b.finishes?.[key] ?? DEFAULT_FINISHES[key]),
	);
}

/** The two-room config the engine applies, materialised onto the input. */
function twoRoomConfigOf(input: BuildInput) {
	return Object.fromEntries(
		(Object.keys(TWO_ROOM_CONFIG_DEFAULTS) as TwoRoomConfigKey[]).map((key) => [
			key,
			twoRoomConfigValue(input, key),
		]),
	) as Pick<BuildInput, TwoRoomConfigKey>;
}

function twoRoomConfigValue(
	input: BuildInput,
	key: TwoRoomConfigKey,
): BuildInput[TwoRoomConfigKey] {
	return input[key] ?? TWO_ROOM_CONFIG_DEFAULTS[key];
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Compare two BuildInput snapshots and return which subsystem groups
 * need to be rebuilt.
 *
 * Returns an empty set when nothing changed (no rebuild needed),
 * or a set containing ALL_DIRTY flags on a full rebuild scenario.
 */
export function computeDirtyFlags(
	prev: BuildInput | null,
	next: BuildInput,
	throughWallVents = false,
): Set<DirtyFlag> {
	// First render or no previous state → full rebuild
	if (!prev) return new Set(ALL_DIRTY);

	const dirty = new Set<DirtyFlag>();

	// Dimensions affect every subsystem
	if (!dimensionsEqual(prev, next)) {
		return new Set(ALL_DIRTY);
	}

	// Openings affect framing, panels and exterior-owned front details
	if (!openingsEqual(prev, next)) {
		addDirtyDependencies(dirty, "openings");
	}

	if (!partitionSplitEqual(prev, next)) {
		addDirtyDependencies(dirty, "partitionSplit");
	}

	if (!deckConfigEqual(prev, next)) {
		addDirtyDependencies(dirty, "deckConfig");
	}

	// Thermal inputs affect interior placement
	if (!thermalEqual(prev, next)) {
		addDirtyDependencies(dirty, "thermalInputs");
	}

	if (!roofConfigEqual(prev, next)) {
		addDirtyDependencies(dirty, "roofConfig");
	}

	if (!siteConditionsEqual(prev, next)) {
		addDirtyDependencies(dirty, "siteConditions");
	}

	if (!interiorLayoutEqual(prev, next)) {
		addDirtyDependencies(dirty, "interiorLayout");
	}

	if (!finishesEqual(prev, next)) {
		addDirtyDependencies(dirty, "finishes");
	}

	if (!indoorWallsEqual(prev, next)) {
		addDirtyDependencies(dirty, "indoorRoom");
	}

	if (
		normalizeIndoorRoom(prev.indoorRoom).houseAboveCeilingM !==
		normalizeIndoorRoom(next.indoorRoom).houseAboveCeilingM
	) {
		addDirtyDependencies(dirty, "houseAboveCeiling");
	}

	for (const key of Object.keys(
		TWO_ROOM_CONFIG_DEFAULTS,
	) as TwoRoomConfigKey[]) {
		if (twoRoomConfigValue(prev, key) !== twoRoomConfigValue(next, key)) {
			addDirtyDependencies(dirty, key);
		}
	}

	// The roof joists are trimmed around a wood stove's flue (skorstensväxling)
	// and the stove is interior geometry. Whenever the interior is rebuilt,
	// ask where the flue now stands: if it moved (or appeared, or went), the
	// roof must be rebuilt too, or its joists keep the opening cut for where
	// the stove used to be. Resolved, not guessed from which fields changed —
	// a layout edit, a heater type, a split or a door nudge can all move it.
	if (dirty.has(DirtyFlag.Interior)) {
		const flue = (input: BuildInput) =>
			resolveFlueAxisM(
				throughWallVents ? { ...input, ...twoRoomConfigOf(input) } : input,
			);
		const a = flue(prev);
		const b = flue(next);
		if (a?.x !== b?.x || a?.z !== b?.z) dirty.add(DirtyFlag.Roof);
	}

	// Two-room vents follow the interior heater; their bores and outer covers must follow too.
	if (throughWallVents && dirty.has(DirtyFlag.Interior)) {
		dirty.add(DirtyFlag.Panel);
		dirty.add(DirtyFlag.Exterior);
	}

	return dirty;
}
